import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { handleRequestHelper } from '../../../utils/helpers';
import api from '../../../api/api';
import { useNotifications } from '../../../Providers/NotificationsProvider/NotificationsProvider';
import { useForm } from '../../../hooks/useForm';
import { copyToClipboard } from '../../../utils/utils';
import { SCAN_OPTION, SCAN_STATE } from './types';
import { NOTIFICATION_VARIANT } from '../../../constants/types';

export const useAdvancedEventSettingsPage = () => {
  const { eventId } = useParams();
  const { showNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [eventSettings, setEventSettings] = useState({});
  const [trackingConfig, setTrackingConfig] = useState({});
  const gateSecretApiKey = useRef('');
  const eventName = useRef('');

  const mapScanStateToOption = (scanOptions) => {
    if (!scanOptions) return;

    const { scanApp, turnstileGate } = scanOptions;
    if (scanApp === SCAN_STATE.BURN && turnstileGate === SCAN_STATE.BURN) return SCAN_OPTION.ALL;
    if (scanApp === SCAN_STATE.BURN) return SCAN_OPTION.SCAN_APP;
    if (turnstileGate === SCAN_STATE.BURN) return SCAN_OPTION.TURNSTILE_GATE;
  };

  const getEventTrackingConfigs = async () => {
    await handleRequestHelper({
      endpoint: () => api.getEventTrackingConfigs(eventId),
      onSuccess: ({ trackingConfigs }) => setTrackingConfig(trackingConfigs[0]),
    });
  };

  const mapOptionToScanState = (scanOption) => {
    switch (scanOption) {
      case SCAN_OPTION.ALL:
        return { scanApp: SCAN_STATE.BURN, turnstileGate: SCAN_STATE.BURN };
      case SCAN_OPTION.TURNSTILE_GATE:
        return { scanApp: SCAN_STATE.VALIDATE, turnstileGate: SCAN_STATE.BURN };
      case SCAN_OPTION.SCAN_APP:
        return { scanApp: SCAN_STATE.BURN, turnstileGate: SCAN_STATE.VALIDATE };
      default:
        return eventSettings.scanOptions;
    }
  };

  const validationModeOptions = [
    { label: 'Molinetes y app de escaneo', value: SCAN_OPTION.ALL },
    { label: 'Solo molinetes', value: SCAN_OPTION.TURNSTILE_GATE },
    { label: 'Solo app de escaneo', value: SCAN_OPTION.SCAN_APP },
  ];

  const metaPixelIdValidation = (value, { metaAccessToken }) => {
    return value && !metaAccessToken ? 'El Access Token es requerido' : '';
  };

  const metaAccessTokenValidation = (value, { metaPixelId }) => {
    return value && !metaPixelId ? 'El Pixel ID es requerido' : '';
  };

  const { formState, handleInputChange, validateFormInputs, formValues } = useForm(
    {
      secondaryMarketEnable: { value: eventSettings.secondaryMarketEnable },
      secondaryMarketEventButtonEnable: {
        value: eventSettings.secondaryMarketEventButtonEnable,
      },
      hoursBeforeRelease: {
        value: eventSettings.hoursBeforeStartForQrsRelease?.primarySales,
      },
      hoursBeforeReleaseSecondaryMarket: {
        value: eventSettings.hoursBeforeStartForQrsRelease?.secondaryMarket,
      },
      scanOptions: { value: mapScanStateToOption(eventSettings.scanOptions) },
      canBeSoldByCustomer: { value: eventSettings.canBeSoldByCustomer },
      metaPixelId: {
        value: trackingConfig?.metaPixelId,
        required: false,
        customValidation: metaPixelIdValidation,
      },
      metaAccessToken: {
        value: trackingConfig?.metaAccessToken,
        required: false,
        customValidation: metaAccessTokenValidation,
      },
      // shouldBlacklistSellers: { value: eventSettings.shouldBlacklistSellers },
      // shouldExtendStopOfSalesToInvitations: {
      //   value: eventSettings.shouldExtendStopOfSalesToInvitations,
      // },
      // shouldConvertPriceForUsd: { value: eventSettings.shouldConvertPriceForUsd },
    },
    [eventSettings, gateSecretApiKey, trackingConfig]
  );

  const getEventSettings = async () => {
    await handleRequestHelper({
      endpoint: () => api.getEventSettings(eventId),
      onSuccess: ({ settings }) => {
        setEventSettings(settings);
      },
      showNotification,
    });
  };

  const getGateSecretApiKey = async () => {
    await handleRequestHelper({
      endpoint: () => api.generateGateSecretApiKey(eventId),
      onSuccess: ({ secret }) => {
        gateSecretApiKey.current = secret;
      },
      showNotification,
    });
  };

  const getEventName = async () => {
    await handleRequestHelper({
      endpoint: () => api.getEventName(eventId),
      onSuccess: ({ name }) => {
        eventName.current = name;
      },
    });
  };

  const getInitialData = async () => {
    setIsLoading(true);
    await Promise.all([
      getEventName(),
      getGateSecretApiKey(),
      getEventTrackingConfigs(),
      getEventSettings(),
    ]);
    setIsLoading(false);
  };

  const copySecretApiKeyToClipboard = () => {
    copyToClipboard(gateSecretApiKey.current, showNotification, 'Secret Api Key copiada!');
  };

  useEffect(() => {
    getInitialData();
  }, []);

  const createOrUpdateEventTrackingConfig = async (newTrackingConfig) => {
    const hasInitialTrackingConfig = trackingConfig?.metaPixelId && trackingConfig?.metaAccessToken;
    const hasNewTrackingConfig = newTrackingConfig.metaPixelId && newTrackingConfig.metaAccessToken;

    if (hasInitialTrackingConfig && !hasNewTrackingConfig) {
      await handleRequestHelper({
        endpoint: () => api.deleteEventTrackingConfig(eventId, trackingConfig.id),
      });
    } else if (!hasInitialTrackingConfig && hasNewTrackingConfig) {
      await handleRequestHelper({
        endpoint: () => api.createEventTrackingConfig(eventId, newTrackingConfig),
      });
    } else if (hasInitialTrackingConfig && hasNewTrackingConfig) {
      await handleRequestHelper({
        endpoint: () =>
          api.updateEventTrackingConfig(eventId, trackingConfig.id, newTrackingConfig),
      });
    }
  };

  const updateEventSettings = async (eventSettings) => {
    const { scanOptions, hoursBeforeRelease, hoursBeforeReleaseSecondaryMarket, ...settings } =
      eventSettings;

    const body = {
      ...settings,
      scanOptions: mapOptionToScanState(scanOptions),
      hoursBeforeStartForQrsRelease: {
        primarySales: hoursBeforeRelease,
        secondaryMarket: hoursBeforeReleaseSecondaryMarket,
      },
    };

    await handleRequestHelper({
      endpoint: () => api.updateEventSettings(eventId, body),
      onSuccess: () =>
        showNotification('Configuración actualizada correctamente', NOTIFICATION_VARIANT.SUCCESS),
      showNotification,
    });
  };

  const handleSubmit = async () => {
    const isFormValid = validateFormInputs();
    if (!isFormValid) return;
    setIsUpdating(true);

    const { metaPixelId, metaAccessToken, ...eventSettings } = formValues;
    const trackingConfig = { metaPixelId, metaAccessToken };

    await Promise.all([
      updateEventSettings(eventSettings),
      createOrUpdateEventTrackingConfig(trackingConfig),
    ]);

    setIsUpdating(false);
  };

  return {
    formState,
    validationModeOptions,
    isLoading,
    gateSecretApiKey: gateSecretApiKey.current,
    eventName: eventName.current,
    handleInputChange,
    handleSubmit,
    copySecretApiKeyToClipboard,
    isUpdating,
  };
};
